/* eslint-disable no-mixed-spaces-and-tabs */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
	addDoc,
	collection,
	doc,
	Timestamp,
	updateDoc,
	query,
	getDocs,
	orderBy,
	where,
} from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import BASE_QUERY from '@/features/baseQuery';
import { dealsApi, getDealFromCache } from '../deals/dealsApi';
import { ticketsApi } from '../claims/ticketsApi';
import { getApi } from '../apis';
import { clientsApi } from '../clients/clientsApi';
import { contactsApi } from '../contacts/contactsApi';
import { factFindApi } from '../factFind/factFindApi';
import { getActingUser } from '../user/userApi';
import { triggerRefresh } from '../table/tasksTableSlice';
import { safelyParseDate } from '@/utils/date';
import { normalizeTaskTimestamp } from '@/utils/kanbanDealSync';
import { delay } from '@/utils/constants';
import { notificationsApi } from '../notifications/notificationsApi';

export const normalizeTaskForCache = (taskObj) => {
	const task = Object.fromEntries(
		Object.entries(taskObj).filter(([key]) => !key.startsWith('_')),
	);
	return {
		...task,
		id: task.id ?? task.objectID ?? task.docId,
		hubspotId: task.hubspotId,
		completed: task.completed ?? task.status !== 'NOT_STARTED',
	};
};

export const getTaskFromCache = (state, taskId) =>
	engagementsApi.endpoints.getTask.select(taskId)(state)?.data;

export const upsertTaskFields = (dispatch, getState, taskId, fields) => {
	if (!taskId || !fields) return;

	const existing = getTaskFromCache(getState(), taskId);
	dispatch(
		engagementsApi.util.upsertQueryData('getTask', taskId, {
			...(existing ?? { id: taskId }),
			...fields,
		}),
	);
};

const findTaskInState = (state, taskId) => {
	const id = String(taskId);
	console.log('Task id', id);
	for (const args of engagementsApi.util.selectCachedArgsForQuery(state, 'listTasks')) {
		const tasks = engagementsApi.endpoints.listTasks.select(args)(state)?.data?.data ?? [];
		const task = tasks.find((t) => String(t.id) === id);
		if (task) return normalizeTaskForCache(task);
	}
	for (const args of engagementsApi.util.selectCachedArgsForQuery(state, 'batchGetDealsTasks')) {
		const task = Object.values(
			engagementsApi.endpoints.batchGetDealsTasks.select(args)(state)?.data ?? {},
		).find((t) => String(t.id) === id);
		if (task) return normalizeTaskForCache(task);
	}
	// const task = dispatch(engagementsApi.endpoints.getTask.init)
	//  engagementsApi.endpoints.getTask
	return getTaskFromCache(state, id);
	// return null;
};

const getEarliestTaskDateMs = (existingDate, newDueDate) => {
	const existingMs = normalizeTaskTimestamp(existingDate);
	const newMs = normalizeTaskTimestamp(newDueDate);

	if (existingMs == null) {
		return newMs;
	}

	if (newMs == null) {
		return existingMs;
	}

	return Math.min(existingMs, newMs);
};

const TASKS_COLLECTION = 'tasks';

const getDateMillis = (date) => safelyParseDate(date).getMilliseconds();

const preemptiveTaskUpdate = async (
	{
		objectType,
		taskId,
		properties,
		clientId,
		selectedDeal,
		attendees,
		startTime,
		endTime,
		isOnlineMeeting,
		address,
		dueDate,
		dealId,
	},
	{ dispatch, getState },
) => {
	const state = getState();

	const taskQueries = engagementsApi.util
		.selectInvalidatedBy(state, ['TASKS'])
		.map(({ endpointName, originalArgs }) => {
			console.log('ENDPOINT NAME', endpointName, 'ORIGINAL ARGS', originalArgs);
			return dispatch(
				engagementsApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
					if (!Array.isArray(draft?.data)) return;

					const taskToUpdate = draft.data.find((t) => t.id === taskId);
					if (
						(originalArgs?.filters?.includes('completed := true') &&
							properties?.status === 'NOT_STARTED') ||
						(originalArgs?.filters?.includes('completed := false') &&
							properties?.status !== 'NOT_STARTED')
					) {
						const index = draft.data.findIndex((t) => t.id === taskId);

						if (index !== -1) {
							draft.data.splice(index, 1);
						}
					} else {
						const updateProperties = {
							...(properties.taskType && { taskType: properties.taskType }),
							...(clientId && { clientId }),
							...(selectedDeal?.dealId && {
								dealId: selectedDeal?.dealId,
							}),
							...((attendees ?? []).length > 0 && { attendees }),
							...(startTime && {
								startTime: safelyParseDate(startTime).getMilliseconds(),
							}),
							...(endTime && { endTime: getDateMillis(endTime) }),
							...(dueDate && { dueDate: getDateMillis(dueDate) }),
							...(address && { address }),
							isOnlineMeeting: Boolean(isOnlineMeeting),
							...(properties?.status && {
								status: properties.status,
								completed: properties.status === 'COMPLETED',
							}),
							...(properties?.body && { body: properties.body }),
							...(properties?.subject && { subject: properties.subject }),
							...(properties?.timestamp && {
								timestamp: getDateMillis(properties.timestamp),
							}),
							...(properties?.ownerDocId && {
								ownerDocId: properties.ownerDocId,
							}),
							...(properties?.ownerId && {
								ownerId: properties.ownerId,
							}),
							...(properties?.priority && { priority: properties.priority }),
							...(objectType && { objectType }),
						};
						if (taskToUpdate) {
							Object.assign(taskToUpdate, updateProperties);
						}
					}
				}),
			);
		});
	const dealTaskQueries = engagementsApi.util
		.selectInvalidatedBy(state, ['ENGAGEMENTS'])
		.filter(({ endpointName }) => endpointName === 'batchGetDealsTasks')
		.map(({ endpointName, originalArgs }) => {
			console.log('ENDPOINT NAME', endpointName, 'ORIGINAL ARGS', originalArgs);
			return dispatch(
				engagementsApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
					const tasksToUpdate = Object.entries(draft ?? {}).find(([dealId, task]) => {
						return task?.id == taskId;
					});
					if (tasksToUpdate) {
						const [dealId] = tasksToUpdate;
						Object.assign(draft[dealId], properties);
					}
				}),
			);
		});
	return { dealTaskQueries, taskQueries };
};

// Creating an API service using RTK Query
export const engagementsApi = createApi({
	reducerPath: 'engagementsApi',
	tagTypes: ['ENGAGEMENTS', 'TASKS', 'TASK', 'ATTACHMENT', 'NOTES', 'NOTE'],
	baseQuery: fetchBaseQuery(BASE_QUERY),
	endpoints: (builder) => ({
		// Lists notes straight from noteApi/Firestore — replaces the old
		// HubSpot-merged getEngagements notes feed. Callers that also need
		// "activities" (calls/emails) now just use [] for those, matching what
		// getEngagements effectively returned for them anyway.
		listNotes: builder.query({
			providesTags: (result) => [
				'NOTES',
				...(result?.data ?? []).map((n) => ({ type: 'NOTE', id: n.id })),
			],
			query: ({ filters = {}, limit, startAfter }) => ({
				url: `${process.env.REACT_APP_NOTE_API}/list`,
				method: 'POST',
				body: { filters: { archived: false, ...filters }, limit, startAfter },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform listNotes error response', response);
				return response;
			},
		}),
		countNotes: builder.query({
			providesTags: ['NOTES'],
			query: ({ filters = {} } = {}) => ({
				url: `${process.env.REACT_APP_NOTE_API}/count`,
				method: 'POST',
				body: { filters: { archived: false, ...filters } },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform countNotes error response', response);
				return response;
			},
		}),
		getTask: builder.query({
			providesTags: (result, error, taskId) => [{ type: 'TASK', id: taskId }],
			queryFn: (taskId, { getState }) => {
				const cached = getTaskFromCache(getState(), taskId);
				return { data: cached };
			},
			// onCacheEntryAdded: async (
			// 	arg,
			// 	{ getCacheEntry, cacheDataLoaded, getState, dispatch },
			// ) => {
			// 	try {
			// 		const res = await cacheDataLoaded;

			// 		const data = getCacheEntry();
			// 		const state = getState();
			// 		const cached = selectCachedTask(getState(), arg);
			// 		// const val = await dispatch(engagementsApi.endpoints.getTask(arg)).unwrap();
			// 		console.log(`[getTask] Cache entry added ${arg}`, {
			// 			data,
			// 			engagementsApi: state.engagementsApi,
			// 			cached,
			// 			// val,
			// 			res,
			// 		});
			// 	} catch {
			// 		//
			// 	}
			// },
		}),
		batchGetDealsTasks: builder.query({
			providesTags: (result, error, { dealIds }) =>
				dealIds.map((dealId) => ({ type: 'ENGAGEMENTS', id: dealId })),
			query: ({ dealIds, nextTaskOnly }) => ({
				url: process.env.REACT_APP_BATCH_GET_DEALS_TASKS,
				method: 'POST',
				body: { dealIds, nextTaskOnly },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform batchGetDealsTasks error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform batchGetDealsTasks response', response);
				return response.data;
			},
		}),
		getAttachment: builder.query({
			providesTags: (result, error, { fileId }) => [{ type: 'ATTACHMENT', id: fileId }],
			query: ({ fileId }) => ({
				url: process.env.REACT_APP_GET_ATTACHMENT,
				method: 'POST',
				body: { fileId },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getAttachment error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform getAttachment response', response);
				const { name, extension, url } = response;
				return {
					fileName: response.extension ? `${name}.${extension}` : name,
					url,
				};
			},
		}),
		listTasks: builder.query({
			providesTags: (result, error, arg) => [{ type: 'TASKS', id: arg.filters }],
			query: ({ filters, includeFields }) => ({
				url: `${process.env.REACT_APP_TASK_API}/list`,
				method: 'POST',
				body: { includeFields, filters },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform listTasks error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform listTasks response', response);
				return response;
			},
		}),
		// Mutation endpoint to update a task
		updateTask: builder.mutation({
			// This tag indicates that this mutation invalidates the list of deals and list of engagements for arg.dealid
			invalidatesTags: (result, error, arg) => [
				// ...(arg.dealId ? [{ type: 'ENGAGEMENTS', id: arg.dealId }] : []),
				{ type: 'TASK', id: arg.taskId },
				// { type: 'TASKS', id: 'LIST' },
				// 'ENGAGEMENTS',
			],
			queryFn: async (
				{
					taskId,
					properties,
					clientId,
					existingClientId,
					existingDeal,
					selectedDeal,
					objectType,
					attendees,
					startTime,
					endTime,
					isOnlineMeeting,
					address,
					dueDate,
					dealId,
					category,
				},
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				const { dealTaskQueries, taskQueries } = await preemptiveTaskUpdate(
					{
						objectType,
						taskId,
						properties,
						clientId,
						selectedDeal,
						attendees,
						startTime,
						endTime,
						isOnlineMeeting,
						address,
						dueDate,
						dealId,
					},
					{ dispatch, getState },
				);
				try {
					const res = await baseQuery({
						url: `${process.env.REACT_APP_TASK_API}/update`,
						method: 'POST',
						body: {
							taskId,
							properties,
							selectedDeal,
							clientId,
							existingClientId,
							existingDeal,
							attendees,
							startTime,
							endTime,
							isOnlineMeeting,
							address,
							dueDate,
							objectType,
							category,
						},
					});
					console.log('res', res);

					if (res.error) {
						throw res.error;
					}

					try {
						const uppercaseType = (objectType ?? '').toUpperCase();
						const api = getApi(uppercaseType);
						const invalidateId = objectType === 'client' ? clientId : dealId;
						if (api && invalidateId) {
							dispatch(
								api.util.invalidateTags([
									{ type: `${uppercaseType}S`, id: 'LIST' },
									{ type: uppercaseType, id: invalidateId },
								]),
							);
						}
					} catch (error) {
						console.log('Error invalidating object API tags', error);
					} finally {
						await delay(5000);
						dispatch(triggerRefresh());
					}
					return { data: res.data };
				} catch (e) {
					taskQueries.forEach((r) => r.undo());
					dealTaskQueries.forEach((r) => r.undo());
					console.error('Error updating task', e);
					return {
						error: {
							status: e?.status ?? 'CUSTOM_ERROR',
							error:
								e?.data?.error ??
								e?.data?.message ??
								e?.error ??
								e?.message ??
								'Error updating task',
						},
					};
				}
			},
			onQueryStarted: async (
				{ taskId, properties },
				{ dispatch, queryFulfilled, getState },
			) => {
				try {
					// Get previous owner id from cache. ownerDocId (Firestore doc id) is
					// the field callers actually pass when reassigning a task — ownerId
					// (legacy hubspotId) is only ever patched alongside it, never alone.
					const previousTask = findTaskInState(getState(), taskId);
					const previousOwnerDocId = previousTask?.ownerDocId?.toString();
					const newOwnerDocId = properties?.ownerDocId?.toString();

					const res = await queryFulfilled;

					if (newOwnerDocId && previousTask) {
						try {
							const userData = await getActingUser(dispatch, getState);
							if (
								String(userData?.userId) !== newOwnerDocId &&
								previousOwnerDocId !== newOwnerDocId
							) {
								// Task assigned to someone else
								await dispatch(
									notificationsApi.endpoints.createNotification.initiate({
										taskId,
										action: 'assigned',
									}),
								).unwrap();
							}
						} catch (e) {
							console.error('Error creating task notification', e);
							//
						}
					}
				} catch (e) {
					//
				}
			},
		}),
		createTask: builder.mutation({
			//objectType as well to specify to associate task to deal or ticket
			// This is a generic mutation for deal and tickets tag indicates that this mutation invalidates the list of engagements for arg.objectType and arg.dealId
			invalidatesTags: (result, error, arg) => [
				'TASKS',
				'ENGAGEMENTS',
				{
					type: 'ENGAGEMENTS',
					id: arg.relationshipCompanyId ?? arg.dealId,
				},
			],
			queryFn: async (
				{
					dealId,
					title,
					dueDate,
					priority,
					notes,
					type,
					ownerDocId,
					objectType,
					clientId,
					contactId,
					relationshipCompanyId,
					graphId,
					graphUserId,
					attendees,
					startTime,
					endTime,
					isOnlineMeeting,
					address,
					category,
				},
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				const taskDueDate = dueDate ?? endTime;
				let dealRowPatch;

				if (dealId && taskDueDate && !relationshipCompanyId) {
					const currentRow = getDealFromCache(getState(), dealId);
					const nextTaskDate = getEarliestTaskDateMs(
						currentRow?.nextTaskDate,
						taskDueDate,
					);

					dealRowPatch = dispatch(
						dealsApi.util.updateQueryData('getDealRow', dealId, (draft) => {
							if (!draft) {
								return;
							}

							draft.nextTaskDate = nextTaskDate;
						}),
					);
				}

				try {
					const res = await baseQuery({
						url: `${process.env.REACT_APP_TASK_API}/create`,
						method: 'POST',
						body: {
							dealId,
							title,
							dueDate,
							priority,
							notes,
							type,
							ownerDocId,
							objectType,
							clientId,
							contactId,
							relationshipCompanyId,
							graphId,
							graphUserId,
							attendees,
							startTime,
							endTime,
							isOnlineMeeting,
							address,
							category,
						},
					});

					return { data: res.data };
				} catch (e) {
					dealRowPatch?.undo();
					console.error('Error creating task', e);
					return {
						error: {
							status: 'CUSTOM_ERROR',
							error: e.message ?? e.data.message ?? 'Error creating task',
						},
					};
				} finally {
					await delay(5000);
					dispatch(triggerRefresh());
				}
			},
			onQueryStarted: async (
				{ clientId, contactId, dealId, ownerDocId, relationshipCompanyId },
				{ dispatch, getState, queryFulfilled },
			) => {
				try {
					const res = await queryFulfilled;

					try {
						if (clientId) {
							dispatch(
								clientsApi.util.invalidateTags([
									{ type: 'CLIENTS', id: 'LIST' },
									{ type: 'CLIENT', id: clientId },
								]),
							);
						}
						if (contactId) {
							dispatch(
								contactsApi.util.invalidateTags([
									{ type: 'CONTACTS', id: 'LIST' },
									{ type: 'CONTACT', id: contactId },
								]),
							);
						}
						if (dealId && !relationshipCompanyId) {
							dispatch(
								dealsApi.util.invalidateTags([
									{ type: 'DEALS', id: 'LIST' },
									{ type: 'DEAL', id: dealId },
									{ type: 'DEAL_ROW', id: dealId },
								]),
							);
							dispatch(
								engagementsApi.util.invalidateTags([
									{ type: 'ENGAGEMENTS', id: dealId },
								]),
							);
							dispatch(
								ticketsApi.util.invalidateTags([
									{ type: 'TICKETS', id: 'LIST' },
									{ type: 'TICKET', id: dealId },
								]),
							);
						}
					} catch (error) {
						console.log('Error invalidating tags', error);
					}

					console.log('Response from createTask', res);
					if (res.data?.taskId) {
						try {
							const userData = await getActingUser(dispatch, getState);
							if (ownerDocId && String(userData?.userId) !== String(ownerDocId)) {
								// Task assigned to someone else
								await dispatch(
									notificationsApi.endpoints.createNotification.initiate({
										taskId: res.data.taskId,
										action: 'assigned',
									}),
								).unwrap();
							}
						} catch (e) {
							console.error('Error creating task notification', e);
							//
						}
					}
				} catch (e) {
					// Error creating task
				}
			},
		}),
		createNote: builder.mutation({
			//generic create Note — callers pass clientId and/or dealId explicitly
			invalidatesTags: ['NOTES'],
			query: ({
				clientId,
				dealId,
				noteBody,
				objectType,
				subject,
				tags,
				clientFolderDriveId,
				driveId,
				parentFolderDriveId,
				folderName,
				folderPath,
				fileId,
				fileName,
				relationshipCompanyId,
			}) => ({
				url: `${process.env.REACT_APP_NOTE_API}/create`,
				method: 'POST',
				body: {
					clientId,
					dealId,
					noteBody,
					objectType,
					subject,
					tags,
					clientFolderDriveId,
					driveId,
					parentFolderDriveId,
					folderName,
					folderPath,
					fileId,
					fileName,
					relationshipCompanyId,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform createNote response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;

					const uppercaseType = (arg.objectType ?? '').toUpperCase();
					const api = getApi(uppercaseType);
					const invalidateId = arg.objectType === 'client' ? arg.clientId : arg.dealId;
					if (api) {
						dispatch(
							api.util.invalidateTags([
								{ type: `${uppercaseType}S`, id: 'LIST' },
								{ type: uppercaseType, id: invalidateId },
							]),
						);
					}
					dispatch(
						factFindApi.util.invalidateTags([
							{ type: 'DEAL_NOTE_SUMMARY', id: invalidateId },
						]),
					);
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		batchUpdateTasks: builder.mutation({
			query: ({ tasks, properties }) => ({
				url: `${process.env.REACT_APP_TASK_API}/update/batch`,
				method: 'POST',
				body: {
					tasks: tasks.map((task) => ({
						taskId: task.id,
						subject: task.subject,
					})),
					properties,
				},
			}),
			transformResponse: (response) => response,
			invalidatesTags: (result, error, { tasks }) => [
				{ type: 'TASKS', id: 'LIST' },
				...tasks.map((task) => ({
					type: 'TASK',
					id: task.id,
				})),
			],
			onQueryStarted: async ({ properties }, { dispatch, queryFulfilled, getState }) => {
				try {
					const { data } = await queryFulfilled;
					const updated = data?.updated ?? [];

					for (const { taskId } of updated) {
						upsertTaskFields(dispatch, getState, taskId, properties);
					}

					if (updated.length > 0) {
						dispatch(triggerRefresh());
					}
				} catch (error) {
					console.log('Error refreshing after batch task update', error);
				}
			},
		}),
		deleteTask: builder.mutation({
			// This tag indicates that this mutation invalidates the list of deals and list of engagements for arg.dealid
			invalidatesTags: (result, error, arg) => ['TASKS', 'ENGAGEMENTS'],
			queryFn: async (
				{ dealId, clientId, taskId, objectType },
				{ getState, dispatch },
				_extraOptions,
				baseQuery,
			) => {
				try {
					const res = await baseQuery({
						url: `${process.env.REACT_APP_TASK_API}/delete`,
						method: 'POST',
						body: { taskId },
					});
					try {
						const uppercaseType = (objectType ?? '').toUpperCase();
						const api = getApi(uppercaseType);
						const invalidateId = objectType === 'client' ? clientId : dealId;
						if (api && invalidateId) {
							dispatch(
								api.util.invalidateTags([
									{ type: `${uppercaseType}S`, id: 'LIST' },
									{ type: uppercaseType, id: invalidateId },
								]),
							);
						}
					} catch (e) {
						console.error('Error invalidating tags after deleting task', e);
					}
					return { data: res.data };
				} catch (e) {
					console.error('Error deleting task', e);
				} finally {
					await delay(5000);
					dispatch(triggerRefresh());
				}
			},
		}),
		//generic delete note mutation
		deleteNote: builder.mutation({
			invalidatesTags: (result, error, arg) => [{ type: 'NOTE', id: arg.noteId }, 'NOTES'],
			query: ({ noteId }) => ({
				url: `${process.env.REACT_APP_NOTE_API}/delete`,
				method: 'POST',
				body: { noteId },
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform deleteNote response', response);
				return response;
			},
			onQueryStarted: async (
				{ clientId, dealId, objectType },
				{ dispatch, queryFulfilled },
			) => {
				try {
					await queryFulfilled;
					const uppercaseType = (objectType ?? '').toUpperCase();
					const api = getApi(uppercaseType);
					const invalidateId = objectType === 'client' ? clientId : dealId;
					if (api) {
						dispatch(
							api.util.invalidateTags([
								{ type: `${uppercaseType}S`, id: 'LIST' },
								{ type: uppercaseType, id: invalidateId },
							]),
						);
					}
				} catch (error) {
					console.log('Error invalidating tags', error);
				}
			},
		}),
		updateNote: builder.mutation({
			invalidatesTags: (result, error, arg) => [{ type: 'NOTE', id: arg.noteId }, 'NOTES'],
			query: ({ noteId, ...body }) => ({
				url: `${process.env.REACT_APP_NOTE_API}/update`,
				method: 'POST',
				body: { noteId, ...body },
			}),
		}),
		// Gets all activities, either pass dealId or clientId
		getAllActivities: builder.query({
			providesTags: (result, error, arg) => [
				{ type: 'ENGAGEMENTS', id: arg.dealId },
				{ type: 'ENGAGEMENTS', id: arg.clientId },
			],
			query: ({ dealId, clientId }) => ({
				url: process.env.REACT_APP_RETRIEVE_ALL_ACTIVITIES,
				method: 'POST',
				body: { dealId, clientId },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getAllActivities error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform getAllActivities response', response);
				return {
					data: response.data,
					page: response.page,
					total: response.total,
				};
			},
		}),
		getDealComplianceTasks: builder.query({
			providesTags: (result, error, arg) => [{ type: 'ENGAGEMENTS', id: arg.dealId }],
			query: ({ dealId, includeInactive }) => ({
				url: process.env.REACT_APP_COMPLIANCE_TASKS,
				method: 'POST',
				body: { dealId, includeInactive },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform getDealComplianceTasks error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform getDealComplianceTasks response', response);
				return response;
			},
		}),
		createActivity: builder.mutation({
			//generic create Note
			// This tag indicates that this mutation invalidates the list of deals and list of engagements for arg.dealid
			invalidatesTags: (result, error, arg) => [
				{ type: 'ENGAGEMENTS', id: arg.dealId },
				{ type: 'ENGAGEMENTS', id: arg.clientId },
				'ENGAGEMENTS',
			],
			queryFn: async (
				{ clientId, properties, dealId, activityType },
				{ getState, dispatch },
			) => {
				try {
					const userData = await getActingUser(dispatch, getState);
					const { userId } = userData ?? {};
					const userRef = doc(firestore, 'user', userId);
					const body = {
						activityType: activityType,
						clientId,
						...properties,
						completedBy: userRef,
						createdDate: Timestamp.now(),
					};
					console.log('BODY for activities', body);
					const res = await addDoc(collection(firestore, 'activities'), body);
					console.log(
						`Successfully created activity doc for ${activityType}`,
						clientId,
						res?.id,
					);
					return { data: { id: res.id } };
				} catch (err) {
					console.log('Error creating activity doc', err);
					return {
						error: {
							data: err.message || 'Unknown error occurred',
						},
					};
				}
			},
		}),
		updateComplianceTask: builder.mutation({
			invalidatesTags: (result, error, arg) => [{ type: 'ENGAGEMENTS', id: arg.dealId }],
			queryFn: async ({ docId, isCompleted, dealId }) => {
				const dateCompleted = isCompleted ? new Date() : null;
				try {
					const docRef = doc(firestore, 'complianceDealTasks', docId);
					console.log('here is the doc ref: ', docRef);
					await updateDoc(docRef, {
						completed: isCompleted,
						...(dateCompleted && { dateCompleted: Timestamp.fromDate(dateCompleted) }),
					});

					console.log(`Successfully updated document ${docId}`);
					return {
						data: docId,
					};
				} catch (err) {
					console.log('Error updating Firebase document', err);
					return {
						error: {
							status: 500,
							data: err.message || 'Unknown error occurred',
						},
					};
				}
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform updateComplianceTask response', response);
				return response;
			},
		}),
		generatePdf: builder.mutation({
			query: ({ htmlContent, fileName }) => ({
				url: process.env.REACT_APP_HTML_TO_PDF,
				method: 'POST',
				body: { htmlContent, fileName },
				responseHandler: async (response) => {
					const blob = await response.blob();
					const blobUrl = URL.createObjectURL(blob);
					return { blobUrl };
				},
				transformResponse: (response) => response,
			}),
			transformErrorResponse: (response) => {
				console.error(':space_invader: Error creating PDF', response);
				return response;
			},
		}),
		getTaskCategories: builder.query({
			queryFn: async () => {
				try {
					const q = query(
						collection(firestore, 'taskCategories'),
						orderBy('order', 'asc'),
					);
					const snapshot = await getDocs(q);
					const categories = snapshot.docs.map((doc) => ({
						id: doc.id,
						...doc.data(),
					}));
					return { data: categories };
				} catch (err) {
					return { error: { status: 'CUSTOM_ERROR', error: err.message } };
				}
			},
		}),
		getTaskCategory: builder.query({
			queryFn: async (category) => {
				if (!category) return { data: null };
				try {
					const q = query(
						collection(firestore, 'taskCategories'),
						where('value', '==', category),
					);
					const snapshot = await getDocs(q);
					if (snapshot.empty) return { data: null };
					return { data: { id: snapshot.docs[0].id, ...snapshot.docs[0].data() } };
				} catch (err) {
					return { error: { status: 'CUSTOM_ERROR', error: err.message } };
				}
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defined endpoints
export const {
	useListNotesQuery,
	useCountNotesQuery,
	useBatchGetDealsTasksQuery,
	useGetAttachmentQuery,
	useListTasksQuery,
	useLazyListTasksQuery,
	useUpdateTaskMutation,
	useBatchUpdateTasksMutation,
	useCreateNoteMutation,
	useCreateTaskMutation,
	useDeleteNoteMutation,
	useUpdateNoteMutation,
	useDeleteTaskMutation,
	useGetAllActivitiesQuery,
	useGetDealComplianceTasksQuery,
	useUpdateComplianceTaskMutation,
	useCreateActivityMutation,
	useGeneratePdfMutation,
	useGetTaskCategoriesQuery,
	useGetTaskCategoryQuery,
} = engagementsApi;
